import mixpanel, { Dict } from 'mixpanel-browser'

export class MixpanelEventLogger {
  private static instance: MixpanelEventLogger | null = null

  private constructor() {
    if (typeof window !== 'undefined') {
      mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN as string, {
        debug: false,
        persistence: 'localStorage',
        persistence_name: 'mp_' + process.env.NEXT_PUBLIC_MIXPANEL_TOKEN,
        loaded: () => {
          console.log('Mixpanel 초기화 완료')
        },
        record_mask_text_selector: '',
      })
      // const deviceId = mixpanel.get_distinct_id()
      // console.log('deviceId:', deviceId)
    }
  }

  static getInstance() {
    if (typeof window === 'undefined') {
      return new MixpanelEventLogger()
    }

    if (!MixpanelEventLogger.instance) {
      MixpanelEventLogger.instance = new MixpanelEventLogger()
    }
    return MixpanelEventLogger.instance
  }

  getUserId(): string {
    return mixpanel.get_distinct_id()
  }

  setUserId(userId: string | undefined) {
    if (userId) {
      // Simplified ID Merge API 방식

      const deviceId = mixpanel.get_distinct_id()

      mixpanel.identify(userId)

      // 2. 사용자 ID와 device_id를 함께 등록
      if (userId && userId !== deviceId) {
        mixpanel.register({
          user_id: userId,
          $user_id: userId,
          $device_id: deviceId,
          $last_seen: new Date().toISOString(),
        })
        // // 3. people 프로필 업데이트
        mixpanel.people.set({
          user_id: userId,
          $user_id: userId,
          $device_id: deviceId,
          $last_seen: new Date().toISOString(),
        })
      }

      // 앱 로깅
      if (window.AndroidBridge && window.AndroidBridge.setUserId) {
        window.AndroidBridge.setUserId(userId)
      }
    }
  }

  setUserProperties(properties: object | null) {
    if (properties && typeof properties === 'object') {
      mixpanel.register(properties)
      mixpanel.people.set(properties)

      // 앱 로깅
      if (window.AndroidBridge && window.AndroidBridge.setUserProperties) {
        Object.entries(properties).map(([key, value]) => {
          window.AndroidBridge.setUserProperty(key, String(value))
        })
      }
    }
  }

  track(
    name: string,
    properties?: Dict,
    optionsOrCallback?: any,
    callback?: any
  ) {
    const userAgent = window?.navigator?.userAgent || ''

    // Mobile device detection
    const isMobile =
      /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|MeeGo|mini|Fennec|Windows Phone|Kindle|Silk|PlayBook|BB10|SamsungBrowser|UCBrowser|Puffin|Dolphin|Safari/i.test(
        userAgent
      )

    // Tablet detection (more specific)
    const isTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobile))/i.test(
      userAgent
    )

    // Desktop detection (more accurate)
    const isDesktop = !isMobile && !isTablet

    // Bot detection (more accurate)
    const isBot =
      // Common bot identifiers
      (/bot[^a-z]|crawler[^a-z]|spider[^a-z]|slurp[^a-z]|phantom[^a-z]|headless[^a-z]|selenium[^a-z]|puppeteer[^a-z]|playwright[^a-z]|scraper[^a-z]/i.test(
        userAgent
      ) ||
        // Automation tools
        /wget[^a-z]|curl[^a-z]|python-requests[^a-z]|java[^a-z]|httpclient[^a-z]|apache-httpclient[^a-z]/i.test(
          userAgent
        ) ||
        // Monitoring and testing tools
        /pingdom[^a-z]|uptimerobot[^a-z]|site24x7[^a-z]|newrelic[^a-z]|datadog[^a-z]|sentry[^a-z]/i.test(
          userAgent
        ) ||
        // Feed readers and aggregators
        /feed[^a-z]|rss[^a-z]|atom[^a-z]|sitemap[^a-z]|aggregator[^a-z]/i.test(
          userAgent
        )) &&
      // Exclude common browsers and apps
      !/chrome[^a-z]|safari[^a-z]|firefox[^a-z]|edge[^a-z]|opera[^a-z]|whale[^a-z]|naver[^a-z]|daum[^a-z]|kakao[^a-z]|samsung[^a-z]|android[^a-z]|ios[^a-z]|mobile[^a-z]/i.test(
        userAgent
      )

    // Additional device type detection
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent)
    const isAndroid = /Android/i.test(userAgent)
    const isWindows = /Windows/i.test(userAgent)
    const isMac = /Macintosh|Mac OS X/i.test(userAgent)
    const isLinux = /Linux/i.test(userAgent)
    const ua = {
      agent: userAgent,
      is_mobile: isMobile,
      is_desktop: isDesktop,
      is_tablet: isTablet,
      is_bot: isBot,
      is_ios: isIOS,
      is_android: isAndroid,
      is_windows: isWindows,
      is_mac: isMac,
      is_linux: isLinux,
    }
    const props = userAgent ? { ...properties, ...ua } : properties
    mixpanel.track(name, props, optionsOrCallback, callback)

    // 앱 로깅
    if (window.AndroidBridge && window.AndroidBridge.logEvent) {
      // name이 40자를 초과할 경우, 40자까지만 사용합니다. firebase event analytics의 name이 40자만 허용하기 때문
      const trimmedName = name.length > 40 ? name.substring(0, 40) : name

      // properties 객체의 모든 값을 문자열로 변환
      const stringifiedProperties = props
        ? Object.entries(props).reduce<{ [key: string]: string }>(
            (acc, [key, value]) => {
              acc[key] = String(value)
              return acc
            },
            {}
          )
        : {}

      window.AndroidBridge.logEvent(
        trimmedName.replace('[', '').replace(']', '').replace(/\s/g, '_'),
        JSON.stringify(stringifiedProperties)
      )
    }
  }

  trackRevenue(_revenuProps: any) {
    console.warn('MixpanelEventLogger.trackRevenue is not implemented')
  }

  reset() {
    mixpanel.reset()
  }

  startSessionRecording() {
    mixpanel.start_session_recording()
  }

  stopSessionRecording() {
    mixpanel.stop_session_recording()
  }
}
